import { useState, useEffect } from 'react';
import { CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { getRecentDailyReports } from '../../../services/firestore/opsService';
import type { DailyHealthReport } from '../../../types/ops';

const C = {
  text: '#18181B', textSec: '#52525B', textMuted: '#A1A1AA',
  rose: '#E11D48', pass: '#059669', warn: '#B45309', fail: '#E11D48',
  bgCard: '#FFFFFF', border: '#E4E4E7',
  passBg: '#D1FAE5', warnBg: '#FEF3C7', failBg: '#FFF1F2',
};

export function HealthChecksTab() {
  const [reports, setReports] = useState<DailyHealthReport[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setReports(await getRecentDailyReports(7));
      } catch (err) {
        console.error('Failed to load health reports:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <Loading />;

  if (reports.length === 0) {
    return <EmptyState message="No health check reports yet. Data will appear after the first daily check at 2:30 AM UTC." />;
  }

  const latest = reports[0];

  return (
    <div>
      {/* Score trend — always show 7 days */}
      <Card title="7-Day Health Score Trend" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', height: 80 }}>
          {Array.from({ length: 7 }, (_, i) => {
            const d = new Date();
            d.setDate(d.getDate() - (6 - i));
            const dateStr = d.toISOString().slice(0, 10);
            const match = reports.find(r => r.date === dateStr);
            const score = match?.score ?? 0;
            const hasData = score > 0;
            const color = hasData
              ? score >= 80 ? C.pass : score >= 50 ? C.warn : C.fail
              : C.border;
            const dayLabel = d.toLocaleDateString('en-US', { weekday: 'short' });
            return (
              <div key={dateStr} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{
                  height: hasData ? Math.max(8, (score / 100) * 50) : 4,
                  background: color,
                  borderRadius: 2,
                  marginBottom: 4,
                  opacity: hasData ? 1 : 0.4,
                }} />
                <span style={{ fontSize: 13, fontWeight: hasData ? 600 : 400, color: hasData ? C.text : C.textMuted }}>
                  {hasData ? score : '–'}
                </span>
                <br />
                <span style={{ fontSize: 11, color: C.textMuted }}>{dayLabel}</span>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Latest check detail */}
      <Card title={`Daily Report — ${latest.date}`}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <Th>Check</Th>
              <Th>Type</Th>
              <Th>Status</Th>
              <Th>Detail</Th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(latest.checks).map(([name, check]) => (
              <tr key={name} style={{ borderBottom: `1px solid ${C.border}` }}>
                <Td style={{ fontWeight: 500 }}>{formatCheckName(name)}</Td>
                <Td>
                  <span style={{
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    color: check.tag === 'COMPLIANCE' ? C.rose : C.textMuted,
                  }}>
                    {check.tag}
                  </span>
                </Td>
                <Td><StatusBadge status={check.status} /></Td>
                <Td style={{ color: C.textSec, fontSize: 13 }}>{check.message}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ── Shared helpers ──

function StatusBadge({ status }: { status: string }) {
  const config = status === 'healthy'
    ? { icon: CheckCircle2, color: C.pass, bg: C.passBg, label: 'HEALTHY' }
    : status === 'degraded'
    ? { icon: AlertTriangle, color: C.warn, bg: C.warnBg, label: 'DEGRADED' }
    : { icon: XCircle, color: C.fail, bg: C.failBg, label: 'CRITICAL' };

  const Icon = config.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 12, fontWeight: 600, color: config.color,
      background: config.bg, padding: '2px 8px', borderRadius: 4,
    }}>
      <Icon size={12} /> {config.label}
    </span>
  );
}

function Card({ title, children, style }: { title: string; children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{
      background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 20,
      ...style,
    }}>
      <p style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, color: C.textMuted, marginBottom: 12 }}>{title}</p>
      {children}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th style={{
      textAlign: 'left', fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
      letterSpacing: 0.5, color: C.textMuted, padding: '8px 12px', borderBottom: `1px solid ${C.border}`,
    }}>
      {children}
    </th>
  );
}

function Td({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <td style={{ padding: '10px 12px', fontSize: 14, color: C.text, ...style }}>{children}</td>;
}

function Loading() {
  return <div style={{ textAlign: 'center', padding: 60, color: C.textMuted, fontSize: 14 }}>Loading health checks...</div>;
}

function EmptyState({ message }: { message: string }) {
  return (
    <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 40, textAlign: 'center' }}>
      <p style={{ fontSize: 14, color: C.textSec }}>{message}</p>
    </div>
  );
}

function formatCheckName(name: string): string {
  return name.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim();
}
